import { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';

import { HANDLING_MODES } from '@savgroup-front-common/constants';
import {
  TRANSPORT_METHOD_TYPE,
  TransportPlan,
} from '@savgroup-front-common/core/api/Claim/getClaimGroupTransportPlanQuery';
import { useGetClaimGroupTransportPlanQuery } from '@savgroup-front-common/core/api/Claim/hooks/useGetClaimGroupTransportPlanQuery';
import { HandlingProcedure } from '@savgroup-front-common/core/api/Claim/setClaimGroupHandlingTransportPlanCommand';
import { useToasts } from '@savgroup-front-common/core/molecules/NotificationsProvider';
import { useRoutedStepsOrchestratorContext } from '@savgroup-front-common/core/molecules/RoutedStepsOrchestrator/RoutedStepsOrchestrator.context';
import { RoutedStepProps } from '@savgroup-front-common/core/molecules/RoutedStepsOrchestrator/RoutedStepsOrchestrator.types';
import {
  AddressInfoDto,
  CUSTOMER_TYPE,
  PickupPoint,
  StrictAutocompleteOption,
} from '@savgroup-front-common/types';

import { ClaimService } from '@/api';
import { useGetClaimGroupConfirmation } from '@/view/app/hooks/useGetClaimGroupConfirmation';
import { useGetOrderDetailsQuery } from '@/view/app/hooks/useGetOrderDetailsQuery';

import { usePickupPointOptions } from '../components/AvailableHandlingMethods/ChooseHandlingMethod/SelectPickupPointDrawerContent/SelectPickupPointDrawerContent.hooks';
import { getNewAvailableMethods } from '../helpers/getNewAvailableMethods';
import { UseGetHandlingByClaimGroup } from '../hooks/useGetHandlingByClaimGroup';
import { useGetStoreById } from '../hooks/useGetStoreById';
import { HandlingValues, IrshStepValues } from '../IrshPages.types';

interface UseClaimGroupDepositPageArgs {
  onNextStep: RoutedStepProps<IrshStepValues>['onNextStep'];
}

const useClaimGroupDepositPage = ({
  onNextStep,
}: UseClaimGroupDepositPageArgs) => {
  const { pushErrors } = useToasts();
  const queryClient = useQueryClient();

  const { values } = useRoutedStepsOrchestratorContext<IrshStepValues>();

  const { orderDetail } = useGetOrderDetailsQuery({
    orderId: values?.order?.orderId,
  });

  const claimGroupId = values?.claimGroupId;
  const storeId = orderDetail?.storePurchasedAt;

  const handlingSummary = values?.handling;

  const { store, isLoading: isStoreLoading } = useGetStoreById({
    sellerId: orderDetail?.sellerId,
    storeId,
  });

  const { claimGroupConfirmation } = useGetClaimGroupConfirmation({
    claimGroupId,
  });

  const { transportPlans, isLoading: isGetTransportPlansLoading } =
    useGetClaimGroupTransportPlanQuery({
      claimGroupId,
    });

  const depositTransportPlans =
    transportPlans?.filter(
      (transportPlan) => transportPlan.handlingMode === HANDLING_MODES.DEPOSIT,
    ) ?? [];

  const depositAvailableMethods = getNewAvailableMethods({
    transportPlans: depositTransportPlans,
    hasHome: handlingSummary?.hasHome,
  });

  const dropAtStoreMethod = depositAvailableMethods.find(
    (method) => method.value === TRANSPORT_METHOD_TYPE.DropAtStoreDeposit,
  );

  const isDropAtStoreMethod =
    dropAtStoreMethod?.value === TRANSPORT_METHOD_TYPE.DropAtStoreDeposit;

  const {
    pickupPoints: pickupPointsDropAtStore,
    isLoading: isPickupPointsLoading,
  } = usePickupPointOptions({
    searchAddress: {
      address: `${store?.address.address}, ${store?.address.city}, ${store?.address.countryCode}`,
      countryCode: store?.address.countryCode,
      distance: 10000,
    },
    availableMethod: dropAtStoreMethod as StrictAutocompleteOption<
      TransportPlan[]
    >,
    isDropAtStore: isDropAtStoreMethod,
  });

  const alreadySelectedMethod = depositAvailableMethods.find(
    (method) =>
      method.value === handlingSummary?.deposit?.transportPlanMethodType,
  );

  const defaultSelectedMethodFromSummary = handlingSummary?.deposit
    ?.transportPlanMethodType
    ? alreadySelectedMethod
    : undefined;
  const defaultAddressFromSummary = handlingSummary?.deposit?.address;
  const defaultPickupPointFromSummary = handlingSummary?.deposit?.pickupPointId
    ? {
        value: handlingSummary?.deposit?.pickupPointId,
      }
    : undefined;

  const [handlingValues, setHandlingValues] = useState<HandlingValues>({
    selectedMethod:
      values?.handlingDeposit?.selectedMethod ??
      (defaultSelectedMethodFromSummary as any),
    selectedPickupPoint:
      values?.handlingDeposit?.selectedPickupPoint ??
      (defaultPickupPointFromSummary as any),
    address: values?.handlingDeposit?.address ?? defaultAddressFromSummary,
  });

  const { mutate: onSubmit, isLoading: isSubmitLoading } = useMutation(
    ['setDepositHandling'],
    async (values: HandlingValues) => {
      const { address, selectedPickupPoint, selectedMethod } = values;

      if (!claimGroupId || !selectedMethod) {
        return undefined;
      }

      const pickupPoint = selectedPickupPoint?.data?.pickupPoint;
      const hasHome =
        selectedMethod.value === TRANSPORT_METHOD_TYPE.HomeIntervention;

      if (hasHome) {
        if (!address) {
          return undefined;
        }

        const responseHandling =
          await ClaimService.setClaimGroupHandlingTransportPlanCommand({
            claimGroupId,
            handlingMode: HANDLING_MODES.HOME,
            address,
          });

        if (responseHandling.failure) {
          pushErrors(responseHandling.errors);

          return undefined;
        }
      } else if (pickupPoint) {
        const responseHandling =
          await ClaimService.setClaimGroupHandlingTransportPlanCommand({
            claimGroupId,
            handlingMode: HANDLING_MODES.DEPOSIT,
            pickupPointId: pickupPoint.id,
            pickupPointAddress: {
              address: pickupPoint.adress,
              additionalAddress: pickupPoint.adress3,
              postalCode: pickupPoint.postalCode,
              city: pickupPoint.city,
              countryCode: pickupPoint.countryCode,
              companyName: pickupPoint.carrierCompany,
              customerType: CUSTOMER_TYPE.NOT_SET,
            },
            pickupPointFullName: pickupPoint.name,
            transportPlanId:
              selectedPickupPoint?.data?.transportPlan.transportPlanId,
            transportPlanHandlingProcedure: HandlingProcedure.Transport,
          });

        if (responseHandling.failure) {
          pushErrors(responseHandling.errors);

          return undefined;
        }
      } else if (address) {
        const responseHandling =
          await ClaimService.setClaimGroupHandlingTransportPlanCommand({
            claimGroupId,
            handlingMode: HANDLING_MODES.DEPOSIT,
            address,
            transportPlanId: selectedMethod?.data?.at(0)?.transportPlanId,
            transportPlanHandlingProcedure: HandlingProcedure.Transport,
          });

        if (responseHandling.failure) {
          pushErrors(responseHandling.errors);

          return undefined;
        }
      } else {
        return undefined;
      }

      await queryClient.invalidateQueries([
        UseGetHandlingByClaimGroup,
        { claimGroupId },
      ]);

      onNextStep({
        newValues: {
          handlingDeposit: values,
        },
      });
    },
  );

  const handleSetPickupPoint = ({
    selectedPickupPoint,
    selectedMethod,
  }: {
    selectedPickupPoint?: StrictAutocompleteOption<{
      pickupPoint: PickupPoint;
      transportPlan: TransportPlan;
    }>;
    selectedMethod: StrictAutocompleteOption<TransportPlan[]>;
  }) => {
    setHandlingValues({
      selectedMethod,
      address: undefined,
      selectedPickupPoint,
      pickupPointSelected: undefined,
    });

    onSubmit({
      selectedMethod,
      address: undefined,
      selectedPickupPoint,
      pickupPointSelected: undefined,
    });
  };

  const handleSetAddress = ({
    address,
    selectedMethod,
  }: {
    address: AddressInfoDto;
    selectedMethod: StrictAutocompleteOption<TransportPlan[]>;
  }) => {
    setHandlingValues({
      selectedMethod,
      address,
      selectedPickupPoint: undefined,
      pickupPointSelected: undefined,
    });

    onSubmit({
      selectedMethod,
      address,
      selectedPickupPoint: undefined,
      pickupPointSelected: undefined,
    });
  };

  return {
    claimGroupId,
    claimGroupConfirmation,
    handling: handlingSummary,
    handlingValues,
    onSubmit,
    handleSetAddress,
    handleSetPickupPoint,
    isSubmitLoading,
    depositAvailableMethods,
    isGetTransportPlansLoading,
    storeAddress: store,
    isLoading:
      isStoreLoading ||
      isPickupPointsLoading ||
      !claimGroupConfirmation ||
      !handlingSummary,
    pickupPointSuggested: pickupPointsDropAtStore?.find(
      (pickupPoint) => pickupPoint.value === store?.id,
    ),
  };
};

export default useClaimGroupDepositPage;
